import React, { useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Check, Sparkles, Copy } from 'lucide-react';
import { peptidePrograms } from '../data/mock';
import { buildLeadPayload } from '../utils/attribution';
import useReveal from '../hooks/useReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * WaitlistCapture — founding-client capture for Peptide Programs.
 * POSTs to /api/leads with source=peptide_waitlist + tags=[founding_client, peptide_<tier>].
 * Lives inside the Peptide Programs section so the context is clear.
 */
const tierOptions = [
  { id: 'starter-sculpt', label: 'Starter Sculpt', price: '$599' },
  { id: 'snatched-signature', label: 'Snatched Signature', price: '$699' },
  { id: 'vip-elite', label: 'VIP Snatched Elite', price: '$999' },
  { id: 'undecided', label: 'Undecided — I want guidance', price: '' },
];

const WaitlistCapture = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tier, setTier] = useState('snatched-signature');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState(false);

  const { ref, visible } = useReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email so we can reach out when your spot opens.');
      return;
    }
    setIsLoading(true);
    try {
      const tierLabel = tierOptions.find((t) => t.id === tier)?.label || 'Undecided';
      const payload = buildLeadPayload(
        {
          email,
          name: name || undefined,
          phone: phone || undefined,
        },
        {
          source: 'peptide_waitlist',
          tags: ['founding_client', `peptide_${tier.replace(/-/g, '_')}`],
        }
      );
      // Stash the preferred tier in name field prefix if no name provided,
      // OR rely on backend to read the tag — keeping it simple: log in tags.
      // (Note: tier preference fully captured in `tags` array.)
      const res = await axios.post(`${API}/leads`, payload);
      setResponse({ ...res.data, tierLabel });
      setSubmitted(true);
    } catch (err) {
      console.error('Waitlist submit failed', err);
      setError('Something went wrong. Please try again or call the studio.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (!response?.couponCode) return;
    navigator.clipboard.writeText(response.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      ref={ref}
      data-testid="peptide-waitlist"
      className={`reveal ${visible ? 'is-visible' : ''} relative bg-onyx border border-blush/30 overflow-hidden`}
    >
      {/* Top hairline accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-blush/50" />

      {/* Soft blush glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(circle at 80% 20%, rgba(236,138,174,0.10) 0%, transparent 55%)',
        }}
      />

      <div className="relative p-9 md:p-12 lg:p-14">
        {!submitted ? (
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: Pitch */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles size={14} className="text-blush" />
                <span className="eyebrow chrome-rose">Founding Client Waitlist</span>
              </div>
              <h3 className="font-serif text-ivory text-3xl md:text-4xl leading-[1.1] mb-5 text-balance">
                Be among the first to begin{' '}
                <span className="brand-italic chrome-rose">your transformation.</span>
              </h3>
              <p className="text-platinum text-sm md:text-base leading-relaxed mb-6">
                Limited spots are available for our first cohort of Peptide Program clients. Founding
                members receive priority onboarding, first access to upcoming program enhancements,
                and a personal welcome ritual from the founder.
              </p>

              <ul className="space-y-2.5">
                {[
                  'Priority onboarding queue',
                  'Founder-led welcome ritual',
                  'First access to new modalities',
                  'Locked-in founding-client pricing',
                ].map((point) => (
                  <li
                    key={point}
                    className="flex items-center gap-3 text-platinum text-sm leading-relaxed"
                  >
                    <Check className="w-3.5 h-3.5 text-blush flex-shrink-0" strokeWidth={2} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <form
              onSubmit={handleSubmit}
              className="lg:col-span-7 space-y-6"
              data-testid="waitlist-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow block mb-3">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 text-sm outline-none transition-colors"
                    data-testid="waitlist-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(323) 555-0123"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 text-sm outline-none transition-colors"
                    data-testid="waitlist-phone"
                  />
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-3">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  required
                  className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 text-sm outline-none transition-colors"
                  data-testid="waitlist-email"
                />
              </div>

              <div>
                <label className="eyebrow block mb-4">Preferred Program</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {tierOptions.map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center justify-between gap-3 border px-4 py-3 cursor-pointer transition-all duration-300 ${
                        tier === opt.id
                          ? 'border-blush bg-carbon/60'
                          : 'border-pewter/60 hover:border-blush/50'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="tier"
                          value={opt.id}
                          checked={tier === opt.id}
                          onChange={(e) => setTier(e.target.value)}
                          className="sr-only"
                          data-testid={`waitlist-tier-${opt.id}`}
                        />
                        <span
                          className={`w-3 h-3 rounded-full border ${
                            tier === opt.id ? 'border-blush bg-blush' : 'border-pewter'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="text-ivory text-sm leading-tight">{opt.label}</span>
                      </span>
                      {opt.price && (
                        <span className="text-silverx text-[10px] tracking-[0.18em] uppercase whitespace-nowrap">
                          {opt.price}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <p className="text-red-300 text-xs" data-testid="waitlist-error">
                  {error}
                </p>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="waitlist-submit"
                  className="btn-pill group w-full sm:w-auto inline-flex justify-center items-center gap-3 px-8 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush disabled:opacity-50"
                >
                  {isLoading ? 'Reserving your spot…' : 'Reserve My Spot'}
                  <ArrowUpRight
                    size={14}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
                <p className="text-silverx/70 text-[10px] tracking-[0.22em] uppercase mt-4">
                  Private. Not shared. Founding spots limited.
                </p>
              </div>
            </form>
          </div>
        ) : (
          /* Success state */
          <div className="text-center max-w-2xl mx-auto" data-testid="waitlist-success">
            <div className="flex items-center justify-center gap-2 mb-5">
              <Sparkles size={14} className="text-blush" />
              <span className="eyebrow chrome-rose">You're on the list</span>
            </div>
            <h3 className="font-serif text-ivory text-3xl md:text-4xl leading-[1.1] mb-5 text-balance">
              Reserved. We'll be in <span className="brand-italic chrome-rose">touch.</span>
            </h3>
            <p className="text-platinum text-sm md:text-base leading-relaxed mb-8 max-w-lg mx-auto">
              Your founding-client spot is reserved for <span className="chrome-rose">{response?.tierLabel}</span>.
              Our concierge will reach out to schedule your private onboarding call.
            </p>

            {response?.couponCode && (
              <div className="border border-dashed border-blush/70 p-6 max-w-sm mx-auto">
                <p className="eyebrow mb-3 text-blush">Welcome Gift · 15% Off Any À La Carte Ritual</p>
                <div className="flex items-center justify-center gap-3">
                  <span
                    className="font-serif text-ivory text-2xl tracking-wider"
                    data-testid="waitlist-coupon-code"
                  >
                    {response.couponCode}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label="Copy code"
                    data-testid="waitlist-coupon-copy"
                    className="p-2 text-silverx hover:text-blush transition-colors"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-silverx mt-3">
                  Valid for 30 days
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistCapture;
